import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../queries';
import { toast } from "react-toastify";
import { 
    fetchAdminSpecialTemplatesApi, 
    downloadSpecialTemplateApi,
    fetchPrescriptionSpecialTemplatesApi 
} from "../../../services/apiService";
import cancelIcon from "../../../assets/images/cancel-btn.svg";

const SpecialTemplatesDownloadModal = ({ isOpen, onClose }) => {
    const queryClient = useQueryClient();
    const [adminTemplates, setAdminTemplates] = useState([]);
    const [doctorTemplates, setDoctorTemplates] = useState([]);
    const [activeTab, setActiveTab] = useState('certificat');
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const categories = [
        { id: 1, title: "Medical Certificate", type: 'certificat' },
        { id: 2, title: "Laboratory Tests", type: 'analyse' },
        { id: 3, title: "Imaging", type: 'imagerie' },
        { id: 4, title: "Referral Letter", type: 'orientation' },
        { id: 5, title: "Specialty", type: 'specialite' }
    ];

    useEffect(() => {
        if (isOpen) {
            fetchAllTemplates();
        }
    }, [isOpen]);

    const fetchAllTemplates = async () => {
        try {
            setLoading(true);
            const [adminRes, doctorRes] = await Promise.all([
                fetchAdminSpecialTemplatesApi(),
                fetchPrescriptionSpecialTemplatesApi()
            ]);
            
            if (adminRes?.data?.res === true) {
                setAdminTemplates(adminRes.data.data || []);
            }
            if (doctorRes?.data?.res === true) {
                setDoctorTemplates(doctorRes.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
            toast.error("Error loading templates");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (templateId) => {
        try {
            setDownloadingId(templateId);
            const response = await downloadSpecialTemplateApi(templateId);
            if (response?.data?.res === true) {
                queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
                toast.success("Template downloaded successfully!");
                fetchAllTemplates();
            } else {
                toast.error(response?.data?.msg || "Error downloading template");
            }
        } catch (error) {
            console.error("Error downloading template:", error);
            toast.error("Error downloading template");
        } finally {
            setDownloadingId(null);
        }
    };

    const isAlreadyDownloaded = (adminTemplateId) => {
        return doctorTemplates.some(t => t.admin_template_id === adminTemplateId);
    };

    const adminTemplatesByCategory = adminTemplates.filter(t => t.type === activeTab);
    const doctorTemplatesByCategory = doctorTemplates.filter(t => t.type === activeTab);

    if (!isOpen) return null;

    return (
        <div className="modal" onClick={onClose} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                width: '90%',
                maxWidth: '900px',
                maxHeight: '85vh',
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{
                    padding: '20px 30px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                        Download templates
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}>
                        <img src={cancelIcon} alt="close" style={{ width: '24px' }} />
                    </button>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f8f9fa',
                    overflowX: 'auto',
                    scrollbarWidth: 'thin',
                    msOverflowStyle: 'none'
                }}>
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveTab(cat.type)}
                            style={{
                                padding: '8px 20px',
                                border: '1px solid',
                                borderColor: activeTab === cat.type ? '#687eff' : '#e2e8f0',
                                borderRadius: '25px',
                                cursor: 'pointer',
                                fontWeight: activeTab === cat.type ? '600' : '500',
                                backgroundColor: activeTab === cat.type ? '#687eff' : '#fff',
                                color: activeTab === cat.type ? '#fff' : '#64748b',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                transition: 'all 0.2s ease',
                                boxShadow: activeTab === cat.type ? '0 2px 8px rgba(104,126,255,0.25)' : 'none',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '38px'
                            }}
                        >
                            {cat.title}
                        </button>
                    ))}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            Loading...
                        </div>
                    ) : (
                        <>
                            {doctorTemplatesByCategory.length > 0 && (
                                <div style={{ marginBottom: '30px' }}>
                                    <h4 style={{ marginBottom: '15px', color: '#52c41a', fontSize: '14px', textTransform: 'uppercase' }}>
                                        ✓ Templates already downloaded
                                    </h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {doctorTemplatesByCategory.map(template => (
                                            <div key={template.id} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '15px 20px',
                                                backgroundColor: '#f6ffed',
                                                border: '1px solid #b7eb8f',
                                                borderRadius: '8px'
                                            }}>
                                                <span style={{ fontWeight: '500' }}>{template.title}</span>
                                                <span style={{ fontSize: '12px', color: '#52c41a' }}>Downloaded</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '14px', textTransform: 'uppercase' }}>
                                    Available templates
                                </h4>
                                {adminTemplatesByCategory.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '40px',
                                        color: '#888',
                                        border: '1px dashed #ddd',
                                        borderRadius: '8px'
                                    }}>
                                        No templates available for this category.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {adminTemplatesByCategory.map(template => {
                                            const downloaded = isAlreadyDownloaded(template.id);
                                            return (
                                                <div key={template.id} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '15px 20px',
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #eaeef1',
                                                    borderRadius: '8px'
                                                }}>
                                                    <div>
                                                        <span style={{ fontWeight: '500', marginRight: '10px' }}>
                                                            {template.title}
                                                        </span>
                                                        {template.is_default === 1 && (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                backgroundColor: '#1890ff',
                                                                color: '#fff',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                marginRight: '8px'
                                                            }}>
                                                                DEFAULT
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleDownload(template.id)}
                                                        disabled={downloadingId === template.id}
                                                        style={{
                                                            padding: '8px 20px',
                                                            borderRadius: '20px',
                                                            border: 'none',
                                                            backgroundColor: '#687eff',
                                                            color: '#fff',
                                                            cursor: 'pointer',
                                                            fontWeight: '500',
                                                            opacity: downloadingId === template.id ? 0.7 : 1
                                                        }}
                                                    >
                                                        {downloadingId === template.id 
                                                            ? '...' 
                                                            : 'Download'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div style={{
                    padding: '15px 20px',
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '10px 30px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SpecialTemplatesDownloadModal;